import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, TOKEN_KEY, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Pencil, Download, Send, Trash2, Loader2, FileText, ScrollText,
  Eye, CheckCircle2, Mail, Copy, Lock, Minimize2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { enqueue } from "@/lib/offline";
import { PhotoImg } from "@/components/PhotoImg";

const OFFLINE_PDF = "Hors ligne : le PDF sera disponible dès le retour du réseau";

function KV({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/30">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 font-mono text-sm font-semibold text-blue-700 dark:text-blue-300">{value || "—"}</p>
    </div>
  );
}

export default function InterventionView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [it, setIt] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [mail, setMail] = useState({ to: "", recipient_name: "", message: "" });
  const [sending, setSending] = useState(false);
  const [compressing, setCompressing] = useState(false);

  const compressPhotos = async () => {
    setCompressing(true);
    try {
      const { data } = await api.post(`/interventions/${id}/compress-photos`);
      const mb = (n) => (n / 1048576).toFixed(1);
      toast.success(data.compressed > 0
        ? `${data.compressed} photo(s) allégée(s) : ${mb(data.before)} Mo → ${mb(data.after)} Mo`
        : "Les photos sont déjà légères");
      load();
    } catch (e) { toast.error("Échec de l'allègement des photos"); } finally { setCompressing(false); }
  };

  const load = () => api.get(`/interventions/${id}`).then((r) => setIt(r.data)).catch(() => {
    toast.error("Introuvable"); navigate("/interventions");
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const openPdf = async () => {
    try {
      const res = await api.get(`/interventions/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPdfUrl(url);
      setPdfOpen(true);
    } catch (e) {
      toast.error(e.response ? "Échec de génération du PDF" : OFFLINE_PDF);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/interventions/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${it.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response ? "Échec du téléchargement du PDF" : OFFLINE_PDF);
    }
  };

  const setStatus = async (status) => {
    try {
      await api.put(`/interventions/${id}`, { ...it, status });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer ce bon d'intervention ?")) return;
    try {
      await api.delete(`/interventions/${id}`);
      toast.success("Supprimé");
      navigate(`/interventions?dossier=${it.folder_id || "none"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendMail = async () => {
    setSending(true);
    try {
      await api.post(`/interventions/${id}/send`, mail);
      toast.success(`PDF envoyé à ${mail.to}`);
      setMailOpen(false);
      load();
    } catch (e) {
      if (!e.response) {
        enqueue({ method: "post", url: `/interventions/${id}/send`, payload: mail });
        toast.success("Hors ligne : l'envoi partira automatiquement au retour du réseau");
        setMailOpen(false);
      } else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/interventions/${id}/duplicate`);
      toast.success("Bon dupliqué (sans signature, daté du jour)");
      navigate(`/interventions/${data.id}/edition`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!it) return <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div>;

  const isAdmin = user?.role === "admin";
  const canEdit = !it.locked || isAdmin;
  const isPv = it.type === "pv_liberation";
  const d = it.data || {};
  const h = it.header || {};

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/interventions?dossier=${it.folder_id || "none"}`)} data-testid="back-btn" className="shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white dark:bg-white dark:text-slate-900">
                {isPv ? <ScrollText className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
              </span>
              <h1 className="font-head text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{it.title}</h1>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs font-semibold text-blue-600">{it.reference}</span>
              <StatusBadge status={it.status} />
              {it.pdf_sent && <span className="inline-flex items-center gap-1 text-xs text-blue-600"><Send className="h-3 w-3" /> Envoyé à {it.sent_to}</span>}
              {it.locked && <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"><Lock className="h-3 w-3" /> Verrouillé{it.locked_at ? ` le ${new Date(it.locked_at).toLocaleString("fr-FR")}` : ""}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2">
        {canEdit && (
          <Button onClick={() => navigate(`/interventions/${id}/edition`)} data-testid="edit-btn" className="h-11 gap-2 bg-amber-500 font-semibold text-slate-900 hover:bg-amber-400">
            <Pencil className="h-4 w-4" /> {isAdmin && it.locked ? "Modifier (admin)" : "Modifier"}
          </Button>
        )}
        <Button onClick={duplicate} variant="outline" data-testid="duplicate-btn" className="h-11 gap-2">
          <Copy className="h-4 w-4" /> Dupliquer
        </Button>
        {(it.photos || []).length > 0 && (
          <Button onClick={compressPhotos} disabled={compressing} variant="outline" data-testid="compress-photos-btn" className="h-11 gap-2">
            {compressing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Minimize2 className="h-4 w-4" />} Alléger les photos
          </Button>
        )}
        <Button onClick={openPdf} variant="outline" data-testid="preview-pdf-btn" className="h-11 gap-2">
          <Eye className="h-4 w-4" /> Aperçu PDF
        </Button>
        <Button onClick={downloadPdf} variant="outline" data-testid="download-pdf-btn" className="h-11 gap-2">
          <Download className="h-4 w-4" /> Télécharger
        </Button>
        <Button onClick={() => setMailOpen(true)} variant="outline" data-testid="send-mail-btn" className="h-11 gap-2">
          <Mail className="h-4 w-4" /> Envoyer par mail
        </Button>
        {it.status !== "termine" && (
          <Button onClick={() => setStatus("termine")} variant="outline" data-testid="mark-termine-btn" className="h-11 gap-2 text-emerald-600">
            <CheckCircle2 className="h-4 w-4" /> Marquer terminé
          </Button>
        )}
        <Button onClick={remove} variant="ghost" data-testid="delete-btn" className="h-11 gap-2 text-red-600 hover:bg-red-50">
          <Trash2 className="h-4 w-4" /> Supprimer
        </Button>
      </div>

      {/* Header info */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="mb-3 font-head text-base font-bold text-slate-900 dark:text-white">Informations chantier</h3>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <KV label="Autoroute / Projet" value={h.autoroute} />
          <KV label="Section" value={h.section} />
          <KV label="PR / Sens" value={h.pr} />
          <KV label="Activité" value={h.activite} />
          <KV label="Date travaux" value={h.date_travaux} />
          <KV label="Rédaction" value={h.date_redaction} />
        </div>
      </section>

      {/* Summary of content */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="mb-3 font-head text-base font-bold text-slate-900 dark:text-white">Contenu</h3>
        <div className="space-y-4 text-sm">
          {isPv ? (
            <>
              <SummaryTable title="Travaux libérés" rows={d.travaux} cols={["nature", "localisation", "observations"]}
                heads={["Nature", "Localisation", "Observations"]} />
              <SummaryTable title="Horaires par entreprise" rows={d.horaires}
                cols={["entreprise", "heure_sortie", "heure_prevue", "heure_restitution", "observations"]}
                heads={["Entreprise", "Sortie", "Prévue", "Restitution", "Obs."]} />
              <TextBlock title="Incidents / observations" value={d.incidents} />
            </>
          ) : (
            <>
              <SummaryTable title="Travaux exécuté / Nature" rows={d.travaux} cols={["nature", "localisation"]} heads={["Nature", "Localisation"]} />
              <SummaryTable title="Linéaire — prévu vs réalisé" rows={d.lineaire} cols={["prevu", "realise", "observations"]} heads={["Prévu", "Réalisé", "Obs."]} />
              <TextBlock title="Observations hygiène sécurité" value={d.hygiene_securite} />
              <TextBlock title="Incidents / observations" value={d.incidents} />
              <TextBlock title="Observations environnement" value={d.environnement} />
            </>
          )}
        </div>
      </section>

      {/* Photos */}
      {(it.photos || []).length > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <h3 className="mb-3 font-head text-base font-bold text-slate-900 dark:text-white">Photos ({it.photos.length})</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {it.photos.map((ph, i) => (
              <div key={i} className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
                <PhotoImg photo={ph} className="aspect-video w-full object-cover" testId={`view-photo-${i}`} />
                {(ph.pk || ph.caption) && <p className="px-2 py-1 text-xs text-slate-500">{ph.pk} {ph.caption}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Signatures */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="mb-3 font-head text-base font-bold text-slate-900 dark:text-white">Signatures</h3>
        <div className={`grid gap-3 ${isPv ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
          {(isPv
            ? [["entreprise", "L'Entreprise"], ["iri", "IR Ingénierie"], ["district", "District"]]
            : [["entreprise", "L'Entreprise"], ["iri", "IR Ingénierie"]]
          ).map(([k, lbl]) => {
            const sig = it.signatures?.[k] || {};
            return (
              <div key={k} className="rounded-xl border border-slate-200 p-3 text-center dark:border-slate-700">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{lbl}</p>
                <div className="mt-2 flex h-20 items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-800">
                  {sig.signature ? <img src={sig.signature} alt="signature" className="max-h-16" /> : <span className="text-xs text-slate-400">Non signé</span>}
                </div>
                <p className="mt-1 text-xs text-slate-500">{sig.name} {sig.date && `· ${sig.date}`}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PDF preview dialog */}
      <Dialog open={pdfOpen} onOpenChange={(o) => { setPdfOpen(o); if (!o && pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Aperçu du PDF — {it.reference}</DialogTitle></DialogHeader>
          {pdfUrl && <iframe title="pdf" src={pdfUrl} className="h-[70vh] w-full rounded-lg border" data-testid="pdf-iframe" />}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPdfOpen(false)} data-testid="close-pdf-btn">Fermer</Button>
            <Button onClick={downloadPdf} className="gap-2"><Download className="h-4 w-4" /> Télécharger</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send mail dialog */}
      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Envoyer le PDF par email</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold text-slate-500">Destinataire (email)</Label>
              <Input type="email" value={mail.to} onChange={(e) => setMail({ ...mail, to: e.target.value })}
                data-testid="mail-to" className="mt-1 h-11" placeholder="client@exemple.com" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500">Nom du destinataire</Label>
              <Input value={mail.recipient_name} onChange={(e) => setMail({ ...mail, recipient_name: e.target.value })}
                data-testid="mail-name" className="mt-1 h-11" placeholder="M. Dupont" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500">Message (optionnel)</Label>
              <textarea value={mail.message} onChange={(e) => setMail({ ...mail, message: e.target.value })}
                data-testid="mail-message" rows={3}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                placeholder="Bonjour, veuillez trouver ci-joint le bon d'intervention..." />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={sendMail} disabled={sending || !mail.to} data-testid="confirm-send-btn"
              className="gap-2 bg-blue-600 text-white hover:bg-blue-500">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryTable({ title, rows, cols, heads }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">{title}</p>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-xs">
          <thead className="bg-slate-900 text-left text-white">
            <tr>{heads.map((hd) => <th key={hd} className="px-3 py-2 font-semibold">{hd}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((r, i) => (
              <tr key={i} className="odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900 dark:even:bg-slate-800/40">
                {cols.map((c) => <td key={c} className="px-3 py-2 font-mono font-semibold text-blue-700 dark:text-blue-300">{r[c] || "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TextBlock({ title, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">{title}</p>
      <p className="whitespace-pre-wrap break-words rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 font-mono text-sm font-medium text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300">{value}</p>
    </div>
  );
}
